import type { de as deTable } from './de';

/** Shape derived from the German table — every leaf widened to `string`
 *  so the literal English values can still be `as const`-typed. Using
 *  `satisfies` below makes TypeScript verify the en table has the same
 *  keys + nesting as de (it errors on missing/extra keys), without
 *  forcing identical string literals. */
type Shape = {
  readonly [K in keyof typeof deTable]: typeof deTable[K] extends string
    ? string
    : {
        readonly [K2 in keyof typeof deTable[K]]: typeof deTable[K][K2] extends string
          ? string
          : { readonly [K3 in keyof typeof deTable[K][K2]]: string };
      };
};

export const en = {
  app: {
    title: 'Cyber-Vault Extraction',
    tagline: 'Neon. Data. Loot.',
  },
  title: {
    startRun: 'Enter Terminal',
    newRunner: 'New Runner',
    continueRunner: 'Continue Runner',
    placeholderName: 'Runner Name',
    create: 'Create',
    cancel: 'Cancel',
  },
  hud: {
    credits: 'Credits',
    premium: 'Quantum',
    cleanCode: 'Clean Code',
    corruptedData: 'Corrupted Data',
    neonGems: 'Neon Gems',
    heat: 'Heat',
    level: 'Level',
    xp: 'XP',
  },
  settings: {
    title: 'Settings',
    language: 'Language',
    actions: 'Actions',
  },
  nav: {
    hero: 'Hero',
    terminal: 'Terminal',
    dungeon: 'Dungeon',
    fight: 'Fight',
    market: 'Market',
    feedback: 'Ideas',
    leaderboard: 'Leaderboard',
    // legacy keys retained for any leftover refs (will be cleaned)
    home: 'Hero',
    inventory: 'Inventory',
    character: 'Hero',
  },
  home: {
    welcomeBack: 'Welcome back,',
    dailyBriefing: 'Daily Briefing',
    quickActions: 'Quick Actions',
    startSiphoning: 'Start Siphoning',
    viewInventory: 'View Loot',
    openMarket: 'Open Market',
    enterDungeon: 'Enter Dungeon',
    stats: 'Stats',
    yieldPerMin: 'Yield / Min',
    activeBoosts: 'Active Boosts',
    noBoosts: 'No active boosts',
    noVaults: 'Boot the terminal to harvest energy, or enter a dungeon.',
    offlineEarnings: 'Offline Earnings',
    offlineEarned: 'While you were away you earned',
  },
  terminal: {
    back: 'Back',
    headline: 'Energy Siphon',
    siphonButton: 'SIPHON',
    charge: 'Charge',
    lastDrop: 'Last Drop',
    nothing: 'Nothing',
    tapToSiphon: 'Tap the Core — active: 3× Credits, idle runs in parallel.',
    shop: 'Auto-Miner Shop',
    yield: 'Passive Yield',
    owned: 'owned',
    lockedLevel: 'Lv {level} required',
    buy: 'Buy',
    insufficient: 'Insufficient resources',
  },
  inventory: {
    headline: 'Runner Inventory',
    resources: 'Resources',
    equipment: 'Equipment',
    empty: 'Inventory empty. Start siphoning or clear a Vault.',
    slotsUsed: 'Slots',
    rarity: {
      common: 'Common',
      uncommon: 'Uncommon',
      rare: 'Rare',
      epic: 'Epic',
      legendary: 'Legendary',
      mythic: 'Mythic',
    },
    value: 'Value',
    equipped: 'Equipped',
    salvage: 'Salvage',
    salvageConfirm: 'Really salvage? You get Scrap + Credits back.',
  },
  market: {
    headline: 'Nano Market',
    comingSoon: 'Marketplace offline',
    description:
      'Runners will soon trade modules, watch live prices and track real-time leaderboards. Until then: stack Credits.',
  },
  dungeon: {
    headline: 'Data-Vaults',
    selectVault: 'Select Vault',
    startRun: 'Start Run',
    levelRequired: 'Level required',
    waves: 'Waves',
    wave: 'Wave',
    flee: 'Leave Vault',
    special: 'OVERRIDE',
    specialReady: 'OVERRIDE READY',
    specialCharging: 'Charging',
    victory: 'VAULT CLEARED',
    defeat: 'RUNNER DOWN',
    loot: 'Loot',
    xpGained: 'XP gained',
    creditsGained: 'Credits gained',
    corruptedDataGained: 'Corrupted Data secured',
    claimLoot: 'Claim Loot',
    retry: 'Retry',
    backToVaults: 'Back to Vaults',
    locked: 'Locked',
    requirement: {
      level: 'Level',
      attack: 'ATK',
      defense: 'DEF',
      health: 'HP',
      slot: 'Slot',
      rarity: 'Rarity',
      weapon: 'Weapon equipped',
      helmet: 'Helmet equipped',
      armor: 'Armor equipped',
      accessory: 'Accessory equipped',
      shield: 'Shield equipped',
      belt: 'Belt equipped',
      sash: 'Sash equipped',
      shoulder: 'Pauldrons equipped',
    },
    tier: 'Tier',
    autoFarm: '🤖 Auto',
    autoFarmOn: '🤖 Auto ON',
    autoFarmOff: '🤖 Auto OFF',
    autoFarmInterval: 'every {interval}',
    autoFarmLocked: 'Clear Vault first',
    useBoost: 'Use Boost',
    boostsActive: 'Active Boosts',
    vaults: {
      vault_alpha: 'Outpost Alpha',
      vault_alpha_desc: 'Abandoned outpost. Tier 1. Security bots and drones on patrol.',
      vault_sigma: 'Lab Sigma',
      vault_sigma_desc: 'Secured research wing. Tier 2. Runners need a weapon.',
      vault_tau: 'Archive Tau',
      vault_tau_desc: 'Data archive. Tier 3. High ATK required.',
      vault_phi: 'Bunker Phi',
      vault_phi_desc: 'Military bunker. Tier 4. Only armored runners survive.',
      vault_omega: 'Nexus Omega',
      vault_omega_desc: 'Endgame. Tier 5. Full epic gear required.',
    },
    enemies: {
      swarmling: 'Swarmling',
      security_bot_mk1: 'Security Bot MK1',
      glitch_drone: 'Glitch Drone',
      patrol_drone: 'Patrol Drone',
      reaver_bot: 'Reaver Bot',
      elite_sentinel: 'Elite Sentinel',
      void_stalker: 'Void Stalker',
      bunker_warden: 'Bunker Warden',
      reactor_titan: 'Reactor Titan',
      nexus_apex: 'Nexus Apex',
      omega_herald: 'Omega Herald',
    },
  },
  character: {
    headline: 'Runner Character',
    stats: 'Combat Stats',
    attack: 'Attack',
    defense: 'Defense',
    hp: 'HP',
    equipment: 'Equipment',
    slots: {
      weapon: 'Weapon',
      helmet: 'Helmet',
      armor: 'Armor',
      accessory: 'Accessory',
      shield: 'Shield',
      belt: 'Belt',
      sash: 'Sash',
      shoulder: 'Pauldrons',
    },
    equip: 'Equip',
    unequip: 'Unequip',
    empty: 'Empty',
    chooseItem: 'Choose Item',
    noItems: 'No matching items in inventory.',
    delete: 'Delete Runner',
    deleteConfirm: 'Really delete Runner? All progress will be lost.',
    memberSince: 'Member since',
    class: 'Class',
    freelancer: 'Freelancer',
    upgrade: 'Upgrade',
    upgradeTo: 'to +{level}',
    maxLevel: 'Max level reached',
    nextLevel: 'Next level',
    cost: 'Cost',
    statPreview: 'Stats after upgrade',
  },
  upgrade: {
    title: 'Upgrade Equipment',
    currentLevel: 'Current',
    insufficient: 'Insufficient resources',
    confirm: 'Upgrade',
    cancel: 'Cancel',
  },
  boosts: {
    combat_stim: 'Combat Stim',
    combat_stim_desc: '+25% damage for 5 minutes',
    shield_matrix: 'Shield Matrix',
    shield_matrix_desc: '+30% defense for 5 minutes',
    nano_medkit: 'Nano Medkit',
    nano_medkit_desc: 'Full heal (instant)',
    lucky_scan: 'Lucky Scan',
    lucky_scan_desc: '+50% loot chance for 10 minutes',
  },
  miners: {
    basic_clicker: 'Basic Clicker',
    basic_clicker_desc: '+1 Credit / sec',
    scrap_hound: 'Scrap Hound',
    scrap_hound_desc: '+1 Scrap every 3 sec',
    chip_miner: 'Chip Miner',
    chip_miner_desc: '+1 Nano-Chip every 10 sec',
    data_harvester: 'Data Harvester',
    data_harvester_desc: '+1 Data-Track every 30 sec',
    quantum_rig: 'Quantum Rig',
    quantum_rig_desc: '+1 Hyper-Processor every 2 min',
  },
  skillWeb: {
    headline: 'Skill Web',
    sub: 'Radial talent tree — Core → Synergy → Capstone',
    coreRing: 'Core Nodes',
    synergyRing: 'Synergy Nodes',
    capstoneRing: 'Capstone (Combat Skills)',
    locked: 'Requirements not met',
    maxed: '✓ Max level',
    research: 'Research',
    nodes: {
      vitality: 'Vitality',
      vitality_desc: '+5 % MaxHP per level',
      alloy: 'Alloy',
      alloy_desc: '+5 % DEF per level',
      aggression: 'Aggression',
      aggression_desc: '+5 % ATK per level',
      data_vampirism: 'Data Vampirism',
      data_vampirism_desc: 'On room change, heals 1 % MaxHP per Lv per 100 CD in the run (cap 25 %)',
      extra_boss_roll: 'Treasure Hunter',
      extra_boss_roll_desc: '+1 boss loot roll per level',
      kinetic_stack: 'Kinetic Absorber',
      kinetic_stack_desc: '+1 DEF stack per hit (max 50)',
      salvage_yield: 'Salvage Expert',
      salvage_yield_desc: '60 → 100 % refund on salvage',
      thermodynamics: 'Thermodynamics',
      thermodynamics_desc: '+25 Max-Heat & +5 % Siphon-Damage per level',
      compound_processor: 'Compound Processor',
      compound_processor_desc: '+1 % ATK per 10 k unprocessed CD in buffer (max +50 %)',
      cap_execute: 'Execute',
      cap_execute_desc: 'Instant-kills enemies below X % HP (Capstone)',
      cap_momentum: 'Momentum',
      cap_momentum_desc: '+X % Special-Charge per kill (Capstone)',
      cap_shield_bash: 'Shield Bash',
      cap_shield_bash_desc: 'Special: DEF × multiplier instead of ATK (Capstone)',
      cap_market_manip: 'Market Manipulation',
      cap_market_manip_desc: 'Decrypter speed boost for 60 s (Capstone)',
      cap_fireball: 'Fireball',
      cap_fireball_desc: 'Projectile, ignores 50 % DEF (Capstone)',
      cap_lightning_bolt: 'Lightning Bolt',
      cap_lightning_bolt_desc: 'Instant, no counter damage (Capstone)',
      cap_frost_lance: 'Frost Lance',
      cap_frost_lance_desc: 'Briefly freezes target (Capstone)',
    },
  },
  decrypters: {
    headline: 'Decrypter Network',
    sub: 'Processes Corrupted Data into Clean Code',
    feedAll: 'Feed All',
    bufferEmpty: 'Buffer empty — feed CD',
    bufferFull: 'Buffer full — expand storage',
    speed: 'Speed',
    storage: 'Storage',
    output: 'Output',
    perMin: '/min',
    per100: 'per 100 CD',
    nanoChipChance: 'Nano-Chip chance',
    doubleStorage: 'Storage ×2 (50 ◇)',
    locked: 'Lv {level} required',
    scrap_parser: 'Scrap Parser',
    scrap_parser_desc: 'Tier 1 — base converter, free',
    chip_extractor: 'Chip Extractor',
    chip_extractor_desc: 'Tier 2 — faster, 5 % Nano-Chip drop',
    quantum_node: 'Quantum Node',
    quantum_node_desc: 'Tier 3 — endgame, 10 % double-chip drop',
  },
  resources: {
    credits: 'Credits',
    premium: 'Quantum',
    cleanCode: 'Clean Code',
    corruptedData: 'Corrupted Data',
    neonGems: 'Neon Gems',
    scrap: 'Scrap',
    energy_cell: 'Energy Cell',
    nano_chip: 'Nano-Chip',
    data_track: 'Data-Track',
    hyper_processor_5: 'Hyper-Processor',
  },
  items: {
    // ── Resources ──────────────────────────────────────────────
    hyper_processor_5: 'Hyper-Processor',
    energy_cell: 'Energy Cell',
    data_track: 'Data-Track',
    nano_chip: 'Nano-Chip',
    scrap: 'Scrap',
    // ── Starter-Kit (common, brand-new character only) ─────────
    starter_blade: 'Practice Blade',
    starter_jacket: 'Recycled Jerkin',
    starter_helmet: 'Drifter Helm',
    // ── Hollow-Knight Set (legendary, sole equipment set) ──────
    hk_armor: 'Hollow Armor',
    hk_mask: 'Hollow Mask',
    hk_cloak: 'Shade Cloak',
    hk_scarf: 'Void Wrap',
    hk_drape: 'Shade Tail',
    hk_heart: 'Soul Heart',
    hk_nail: 'Hollow Nail',
    hk_shield: 'Soul Shield',
    // ── Vault-Knight Set (P3 — Stormcaller-Affinity, storm/lightning theme) ──
    vk_armor: 'Storm Plate',
    vk_helm: 'Voltage Helm',
    vk_pauldrons: 'Thunder Pauldrons',
    vk_sash: 'Tempest Sash',
    vk_belt: 'Static Belt',
    vk_relic: 'Storm Relic',
    vk_blade: 'Stormcaller Blade',
    vk_buckler: 'Aegis Buckler',
    // ── Legendary (boss-exclusive) ─────────────────────────────
    legendary_sword: 'Singularity Blade',
    legendary_staff: 'Arcane Staff',
    legendary_lightning: 'Zeus Bolt',
    legendary_amulet: 'Warden Amulet',
  },
  skills: {
    execute: 'Execute',
    execute_desc: 'Instant-kills enemies below {pct}% HP. Halved against bosses.',
    momentum: 'Momentum',
    momentum_desc: 'Each kill charges the special attack by {pct}%.',
    shield_bash: 'Shield Bash',
    shield_bash_desc: 'Special attack deals {mult}× DEF as damage. CD: 8 ticks.',
    kinetic_absorption: 'Kinetic Absorption',
    kinetic_absorption_desc: 'Hits taken grant +{n} DEF for this run (max 50).',
    market_manipulation: 'Market Manipulation',
    market_manipulation_desc: 'Activates {mult}× miner yield for 60 seconds.',
    compounding_interest: 'Compounding Interest',
    compounding_interest_desc: '+1% income per {k} Credits on account (max +50%).',
    treasure_hunter: 'Treasure Hunter',
    treasure_hunter_desc: 'Boss loot rolls {n}× instead of 1×.',
    salvage_expert: 'Salvage Expert',
    salvage_expert_desc: 'Salvage returns {pct}% of the resources.',
    fireball: 'Fireball',
    fireball_desc: 'Launches a fireball: {mult}× damage, ignores 50% DEF.',
    lightning_bolt: 'Lightning Bolt',
    lightning_bolt_desc: 'Instant lightning bolt: {mult}× damage, no counter.',
    frost_lance: 'Frost Lance',
    frost_lance_desc: 'Frost-lance throw: {mult}× damage, briefly freezes target.',
    frost_nova: 'Frost Nova',
    frost_nova_desc: 'AOE frost burst: {mult}× ATK, +30% DEF for 5s, applies Slow.',
    phantom_burst: 'Phantom Burst',
    phantom_burst_desc: 'Phantom strike: {mult}× damage with guaranteed crit, hits up to 4 nearby.',
    engineer_overload: 'Overload',
    engineer_overload_desc: 'Overload: {mult}× damage, ignores 60% defense.',
    activeSkill: 'Active Skill',
    passiveSkill: 'Passive Skill',
    noSkill: 'No Skill',
    locked: 'Unlocks at Vault Tier {tier}',
    activate: 'Activate',
  },
  sets: {
    phantom_strike: 'Phantom Strike',
    titans_shell: 'Titan Shell',
    scrap_baron: 'Scrap Baron',
    probability_matrix: 'Probability Matrix',
    pieces: '{n}/4 pieces',
    bonus_2pc: '2-piece bonus',
    bonus_4pc: '4-piece bonus',
  },
  ascension: {
    title: '✦ Ascend to Legendary',
    button: 'Ascend',
    notAffordable: 'Insufficient resources',
    ascendedLabel: 'Ascended · Legendary',
    requirement: 'Only for set items at +10',
  },
  leaderboard: {
    headline: '🏆 Leaderboard',
    categoryItemPower: 'Item Power',
    categoryLevel: 'Level',
    categoryVault: 'Vault Tier',
    categoryCredits: 'Credits',
    categoryEndless: 'Endless',
    you: '← You',
    outsideTop: 'You are rank {rank}',
    refresh: 'Refresh',
    loading: 'Loading leaderboard …',
    empty: 'No entries yet.',
    error: 'Failed to load leaderboard.',
  },
  legendaryEffects: {
    armor_penetration: '⚔ Armor Penetration: Fully ignores enemy DEF. +35% damage taken.',
    death_prevention: '💀 Death Prevention: Prevents death once per run (heals 50% HP).',
    credits_to_atk: '◈ Wealth ATK: +1 ATK per 500,000 Credits on account (max +200).',
    gambler_fallacy: '🎲 Gambler\'s Fallacy: +0.2% Legendary chance per hit without a Legendary drop.',
    arcane_focus: '🔮 Arcane Focus: +50% damage on the first hit against a fresh enemy (full HP).',
    chain_lightning: '⚡ Chain Lightning: 30% chance to hit another enemy for 60% damage.',
  },
  boss: {
    alpha: 'Rogue Warden',
    sigma: 'Iron Reaver',
    tau: 'Void Sentinel',
    phi: 'Titan Core',
    omega: 'Omega Herald Prime',
  },
  research: {
    combat_overdrive: 'Combat Overdrive',
    combat_overdrive_desc: 'Increases permanent attack.',
    neural_shield: 'Neural Shield',
    neural_shield_desc: 'Increases permanent defense.',
    bio_enhancement: 'Bio Enhancement',
    bio_enhancement_desc: 'Increases permanent max HP.',
    data_compression: 'Data Compression',
    data_compression_desc: 'Permanently increases XP gain.',
    credit_siphon: 'Credit Siphon',
    credit_siphon_desc: 'Increases Credits per terminal click.',
    quantum_harvest: 'Quantum Harvest',
    quantum_harvest_desc: 'Increases loot drop chance.',
  },
} as const satisfies Shape;

export type I18n = typeof en;

export function itemName(nameKey: string): string {
  const table = en.items as Record<string, string>;
  return table[nameKey] ?? nameKey;
}

export function rarityLabel(rarity: keyof typeof en.inventory.rarity): string {
  return en.inventory.rarity[rarity];
}

export function enemyName(id: string): string {
  const table = en.dungeon.enemies as Record<string, string>;
  return table[id] ?? id;
}

export function vaultName(id: string): string {
  const table = en.dungeon.vaults as Record<string, string>;
  return table[id] ?? id;
}

export function vaultDesc(id: string): string {
  const descKey = `${id}_desc` as keyof typeof en.dungeon.vaults;
  const table = en.dungeon.vaults as Record<string, string>;
  return table[descKey] ?? '';
}

export function resourceLabel(id: string): string {
  const table = en.resources as Record<string, string>;
  return table[id] ?? id;
}

export function minerName(id: string): string {
  const table = en.miners as Record<string, string>;
  return table[id] ?? id;
}

export function minerDesc(id: string): string {
  const key = `${id}_desc`;
  const table = en.miners as Record<string, string>;
  return table[key] ?? '';
}

export function boostName(id: string): string {
  const table = en.boosts as Record<string, string>;
  return table[id] ?? id;
}

export function boostDesc(id: string): string {
  const key = `${id}_desc`;
  const table = en.boosts as Record<string, string>;
  return table[key] ?? '';
}
